import axios from "axios";
import { getCookie } from "../utilities/cookies";

const APP_BACKENDS = {
  hr: "https://api.hr.clikkle.com",
  projects: "https://api.projects.clikkle.com",
  project: "https://api.projects.clikkle.com",
  chat: "https://api.chat.clikkle.com",
  campaigns: "https://api.campaigns.clikkle.com",
  campaign: "https://api.campaigns.clikkle.com",
  worksuite: "https://api.app.clikkle.com",
  app: "https://api.app.clikkle.com",
};

export const isOrgBasedApp = (type) => {
  return [
    "hr",
    "projects",
    "project",
    "chat",
    "campaigns",
    "campaign",
    "worksuite",
    "app",
  ].includes(type?.toLowerCase());
};

const parseSizeToNumber = (label) => {
  if (!label) return 1;

  const rangeMatch = label.match(/(\d+)\s*-\s*(\d+)/);
  if (rangeMatch) return Number(rangeMatch[2]);

  const aboveMatch = label.match(/(\d+)\s*and\s*above/i);
  if (aboveMatch) return Number(aboveMatch[1]);

  const singleMatch = label.match(/\d+/);
  return singleMatch ? Number(singleMatch[0]) : 1;
};

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getCurrentCheckoutUrl = () => {
  if (typeof window === "undefined") return "";

  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
};

export const provisionOrganization = async (type, userId, userData = {}) => {
  const appType = type?.toLowerCase();
  const sourceApp = appType;
  const creationMethod = userData?.creationMethod || "automatic";
  const baseUrl = APP_BACKENDS[appType];
  const accessToken =
    getCookie("clikkleStoreAccessToken") ||
    getCookie("refreshToken") ||
    (typeof window !== "undefined" ? localStorage.getItem("refreshToken") : "");

  if (!baseUrl) {
    throw new Error(`No backend URL defined for app type: ${type}`);
  }

  if (!accessToken || accessToken === "undefined") {
    console.error("No central identity bearer available for provisioning");
    throw new Error("Authentication required. Please log in again.");
  }

  const orgName = `${userData.firstName || userId?.firstName || "User"}'s Workspace`;
  let projUser = null;

  if (isOrgBasedApp(appType)) {
    try {
      const loginPayload = {
        userName: userId.username || userId.userName || "",
        recoveryEmail: userId.recoveryEmail || userId.email || "",
        firstName: userId.firstName || userId.fullName?.split(" ")[0] || "",
        lastName:
          userId.lastName ||
          userId.fullName?.split(" ").slice(1).join(" ") ||
          "",
      };

      const loginResponse = await axios.post(
        "https://api.projects.clikkle.com/api/v2/auth/login",
        loginPayload
      );

      if (loginResponse.data && loginResponse.data.userDetails) {
        projUser = loginResponse.data.userDetails;
      }
    } catch (error) {
      console.error(
        "Login to projects failed:",
        error.response?.data || error.message
      );

      throw new Error("Failed to authenticate with Projects service.");
    }
  }

  let logoBlob = null;

  try {
    const logoResponse = await fetch("/clikkle.png");

    if (logoResponse.ok) {
      logoBlob = await logoResponse.blob();
    }
  } catch (error) {
    console.error("Failed to fetch local logo:", error);
  }

  try {
    let response;

    if (isOrgBasedApp(appType)) {
      const orgSizeNumber = parseSizeToNumber(
        userData.organizationSize || "0 - 15"
      );

      const portalName = `app.clikkle.com/${orgName
        .toLowerCase()
        .replace(/\s+/g, "")}`;

      let image =
        "https://cdn.clikkle.com/images/clikkle/logo/2025/clikkle.png";

      if (logoBlob) {
        try {
          image = await blobToBase64(logoBlob);
        } catch (error) {
          console.error("Error converting logo blob to base64:", error);
        }
      }

      const accountId = userId?._id || userId;

      const recoveryEmail =
        userData.recoveryEmail ||
        userId.recoveryEmail ||
        userData.email ||
        userId.email ||
        "";

      const checkoutUrl = userData.checkoutUrl || getCurrentCheckoutUrl();

      const planId = userData.planId || userData.plan?._id || userData.plan || "";

      const organizationData = {
        name: orgName,

        appType: "worksuite",
        sourceApp,
        creationMethod,

        checkoutCompleted: false,
        paymentStatus: "unpaid",
        checkoutStatus: "pending",
        checkoutUrl,
        planId,

        recoveryEmail,

        Email: recoveryEmail,
        Size: orgSizeNumber,
        Website: "https://worksuite.clikkle.com",
        Address: "Not Provided",
        Country: "Not Provided",
        ZipCode: "00000",
        City: "Not Provided",
        CompanyIndustry: "Other",
        logo: image,
        createdAt: new Date().toISOString(),

        userId: accountId,
        ownerId: accountId,
        owner: accountId,

        email: recoveryEmail,
        plan: "Starter",
        portalName,
        organizationSize: userData.organizationSize || "0 - 15",
        industry: "Other",
        website: "https://worksuite.clikkle.com",
        status: true,
        folderId: accountId,

        imageUrl: image,
        members: accountId ? [accountId] : [],
        type: "Owner",
        totalEmployees: 1,
        projectUserId: projUser?._id,
      };

      console.log("Payload Content:", organizationData);

      response = await axios.post(
        `${APP_BACKENDS.worksuite}/api/organizations`,
        organizationData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
    }

    if (response && response.data) {
      const data = response.data;

      console.log(`Provisioning response for ${appType}:`, data);

      const orgId =
        data.orgId ||
        data.organizationId ||
        data._id ||
        data.organization?._id ||
        data.data?._id ||
        (data.success && data.organization?._id) ||
        data.data?.orgId ||
        data.data?.main?._id;

      if (orgId) {
        localStorage.setItem("orgId", orgId);
        return orgId;
      }
    }

    throw new Error("Invalid response from provisioning API");
  } catch (error) {
    console.error(
      `Provisioning failed for ${appType}:`,
      error.response?.data || error.message
    );

    throw error;
  }
};
